import type {
  OnboardingStep as OnboardingStepType,
  OnboardingWelcomeVariant
} from '~/types/onboarding';
import { OnboardingStep } from '~/types/onboarding';

const DEBUG_QUERY_KEY = 'onboardingDebug';
const DEBUG_STEP_QUERY_KEY = 'onboardingStep';
const DEBUG_NAME_QUERY_KEY = 'onboardingName';
const DEBUG_COMPANY_QUERY_KEY = 'onboardingCompany';
const DEBUG_WELCOME_VARIANT_QUERY_KEY = 'onboardingWelcomeVariant';

export const ONBOARDING_DEBUG_WELCOME_DEFAULTS = {
  firstName: 'Alex',
  lastName: 'Müller',
  company: 'Acme Vision GmbH'
} as const;

export const ONBOARDING_DEBUG_WELCOME_FULL_NAME =
  `${ONBOARDING_DEBUG_WELCOME_DEFAULTS.firstName} ${ONBOARDING_DEBUG_WELCOME_DEFAULTS.lastName}` as const;

export interface OnboardingDebugState {
  isActive: boolean;
  initialStep: OnboardingStepType | null;
  welcomeName: string | null;
  welcomeCompany: string | null;
  welcomeVariant: OnboardingWelcomeVariant | null;
}

function parseTruthyQuery(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true';
}

function parseQueryString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseDebugStep(value: unknown): OnboardingStepType | null {
  const step = parseQueryString(value);
  if (!step) {
    return null;
  }

  const match = Object.values(OnboardingStep).find(
    (candidate: OnboardingStepType) => candidate === step
  );

  return match ?? null;
}

function parseWelcomeVariant(value: unknown): OnboardingWelcomeVariant | null {
  const variant = parseQueryString(value);
  if (variant === 'newRegistration' || variant === 'returningIncomplete') {
    return variant;
  }

  return null;
}

/**
 * Resolves onboarding debug query state.
 *
 * @param allowDebug - true in local dev (`import.meta.dev`) or Contentful
 *   preview stages (`runtimeConfig.public.isPreviewEnabled`). Live production
 *   builds must keep this false so `?onboardingDebug=1` stays inert.
 */
export function resolveOnboardingDebugState(
  query: Record<string, unknown>,
  allowDebug = import.meta.dev
): OnboardingDebugState {
  const isActive = allowDebug && parseTruthyQuery(query[DEBUG_QUERY_KEY]);

  if (!isActive) {
    return {
      isActive: false,
      initialStep: null,
      welcomeName: null,
      welcomeCompany: null,
      welcomeVariant: null
    };
  }

  return {
    isActive: true,
    initialStep: parseDebugStep(query[DEBUG_STEP_QUERY_KEY]),
    welcomeName:
      parseQueryString(query[DEBUG_NAME_QUERY_KEY]) ??
      ONBOARDING_DEBUG_WELCOME_FULL_NAME,
    welcomeCompany:
      parseQueryString(query[DEBUG_COMPANY_QUERY_KEY]) ??
      ONBOARDING_DEBUG_WELCOME_DEFAULTS.company,
    welcomeVariant: parseWelcomeVariant(query[DEBUG_WELCOME_VARIANT_QUERY_KEY])
  };
}
